import { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { expValEquals } from '@confluence/feature-experiments';
import {
	type ContentExtensionMetrics,
	retrieveContentExtensionMetricsFromNode,
} from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers.ts';

import {
	traverseWithNestedContent,
	traverseWithNestedContentAndDepth,
} from './traverseWithNestedContent.ts';

export type AdfStats = {
	contentExtensionMetrics: ContentExtensionMetrics;
	nodeCountByType: Record<string, number>;
	totalNodeCount: number;
};

type CustomPerfData = { contentExtensionMetrics?: ContentExtensionMetrics } | null | undefined;

export function useAdfStats(adfDocument: DocNode, customPerfData?: CustomPerfData): AdfStats {
	const shouldRetrieveContentExtensionMetrics = Boolean(customPerfData);

	const adfStats = useMemo(() => {
		const contentExtensionMetrics: ContentExtensionMetrics = {
			contentExtensionCount: {
				connect: {},
				forge: {},
			},
			contentExtensionKeys: {
				connect: [],
				forge: [],
			},
			depth: {
				connect: [],
				forge: [],
			},
			hasExtensions: false,
		};
		const nodeCountByType: Record<string, number> = {};
		let totalNodeCount = 0;

		if (expValEquals('confluence_instrument_macro_depth', 'isEnabled', true)) {
			traverseWithNestedContentAndDepth(adfDocument, (node, depth) => {
				nodeCountByType[node.type] = (nodeCountByType[node.type] || 0) + 1;
				totalNodeCount++;

				if (shouldRetrieveContentExtensionMetrics) {
					retrieveContentExtensionMetricsFromNode(node, contentExtensionMetrics, depth);
				}
			});
		} else {
			traverseWithNestedContent(adfDocument, (node) => {
				nodeCountByType[node.type] = (nodeCountByType[node.type] || 0) + 1;
				totalNodeCount++;

				if (shouldRetrieveContentExtensionMetrics) {
					retrieveContentExtensionMetricsFromNode(node, contentExtensionMetrics);
				}
			});
		}

		return {
			contentExtensionMetrics,
			nodeCountByType,
			totalNodeCount,
		};
	}, [adfDocument, shouldRetrieveContentExtensionMetrics]);

	if (shouldRetrieveContentExtensionMetrics) {
		customPerfData!.contentExtensionMetrics = adfStats.contentExtensionMetrics;
	}

	return adfStats;
}
